import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  Menu,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import numeral from "numeral";

import CurrencyIcon from "components/currency-icon";
import { useAccountQuery } from "features/auth/authApi";
import {
  BalanceResponseDto,
  useBalancesQuery,
} from "features/finance/financeApi";
import { errorNotification } from "features/notifications/notificationsSlice";
import { updateUser } from "service/api";
import { UsdEur } from "types/usd-eur";
import theme from "theme";

type Props = {
  isSmall?: boolean;
};

const tabular = {
  fontVariantNumeric: "tabular-nums" as const,
  fontFeatureSettings: '"tnum"' as const,
};

const fiatFmt = (value: number | undefined, currency: string) => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(Number(value || 0));
  } catch {
    return `${numeral(value || 0).format("0,0.00")} ${currency.toUpperCase()}`;
  }
};

const cryptoFmt = (value: number | undefined) => {
  const n = Number(value || 0);
  const fractionDigits = n === 0 ? 0 : n >= 1 ? 4 : 8;
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  });
};

const pickFiatValue = (
  summary: BalanceResponseDto | undefined,
  currency: string
) => {
  if (!summary) return 0;
  const c = currency.toLowerCase();
  if (c === "usd") return summary.availableUsd;
  if (c === "eur") return summary.availableEur;
  return summary.availableUsd;
};

const AssetGlyph: React.FC<{ asset: string; size?: number }> = ({
  asset,
  size = 22,
}) => (
  <Box
    sx={{
      width: size + 6,
      height: size + 6,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "background.default",
      flexShrink: 0,
    }}
  >
    <CurrencyIcon currency={asset} size={size} />
  </Box>
);

const PortfolioSummary: React.FC<Props> = ({ isSmall: forceSmall }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isSmall = useMediaQuery(theme.breakpoints.down("md")) || forceSmall;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [ccyAnchor, setCcyAnchor] = useState<null | HTMLElement>(null);
  const ccyOpen = Boolean(ccyAnchor);
  const [switching, setSwitching] = useState(false);

  const {
    data: balances,
    isLoading,
    refetch: refetchBalances,
  } = useBalancesQuery(undefined, {
    pollingInterval: 60000,
  });
  const { data: account, refetch: refetchAccount } = useAccountQuery(
    undefined,
    {
      pollingInterval: 60000,
    }
  );

  const defaultCurrency =
    account?.user.currency ||
    account?.user.kycProgram.default_currency ||
    "usd";
  const ccyKey = (defaultCurrency || "usd").toLowerCase();
  const availableCurrencies = useMemo(
    () => account?.user.kycProgram.fiat_currencies || ["usd", "eur"],
    [account]
  );

  const totalFiatSum = useMemo(() => {
    if (!balances?.newArchBalances) return 0;
    if (ccyKey === "eur") return balances.newArchBalances.availableEurSum;
    if (ccyKey === "usd") return balances.newArchBalances.availableUsdSum;
    return balances.newArchBalances.availableUsdSum;
  }, [balances, ccyKey]);

  const supportedCryptos = useMemo(
    () => account?.user.kycProgram.supported_cryptos || [],
    [account]
  );

  const orderedAssets = useMemo(() => {
    const summaryMap = new Map<string, BalanceResponseDto>();
    balances?.newArchBalances?.summary?.forEach((s) => {
      summaryMap.set(s.asset.toString(), s);
    });

    return supportedCryptos
      .map((asset) => {
        const summary = summaryMap.get(asset);
        return {
          asset,
          summary,
          fiatValue: pickFiatValue(summary, ccyKey),
        };
      })
      .sort((a, b) => b.fiatValue - a.fiatValue);
  }, [supportedCryptos, balances, ccyKey]);

  const topHoldings = useMemo(
    () => orderedAssets.filter((a) => a.fiatValue > 0).slice(0, 3),
    [orderedAssets]
  );

  const getRate = (asset: string) => {
    const ratesForAsset =
      balances?.rates?.[asset as keyof NonNullable<typeof balances.rates>];
    if (!ratesForAsset) return 0;
    const k = ccyKey as keyof UsdEur;
    return Number(ratesForAsset[k] || 0);
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);
  const openCcy = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setCcyAnchor(e.currentTarget);
  };
  const closeCcy = () => setCcyAnchor(null);

  const handleSwitchCurrency = async (next: string) => {
    closeCcy();
    if (next.toLowerCase() === ccyKey) return;
    try {
      setSwitching(true);
      await updateUser({ currency: next.toLowerCase() });
      await refetchAccount();
      await refetchBalances();
    } catch (e: any) {
      dispatch(
        errorNotification(e?.message || t("profile.messages.something_wrong"))
      );
    } finally {
      setSwitching(false);
    }
  };

  const heroLabel = !isSmall && (
    <Typography variant="sectionLabel" sx={{ color: "text.secondary" }}>
      {t("balance.total_balance")}
    </Typography>
  );

  const amountText = isLoading ? (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ minHeight: isSmall ? 22 : 28 }}
    >
      <CircularProgress size={14} thickness={5} />
      <Typography
        variant={isSmall ? "heroAmountSm" : "heroAmount"}
        sx={{ color: "text.disabled" }}
      >
        {fiatFmt(totalFiatSum, ccyKey)}
      </Typography>
    </Stack>
  ) : (
    <Typography
      variant={isSmall ? "heroAmountSm" : "heroAmount"}
      sx={{ color: "text.primary" }}
    >
      {fiatFmt(totalFiatSum, ccyKey)}
    </Typography>
  );

  const currencyChip = (
    <ButtonBase
      onClick={openCcy}
      disabled={switching}
      sx={{
        px: 0.75,
        py: 0.25,
        borderRadius: 999,
        bgcolor: "action.hover",
        "&:hover": { bgcolor: "action.selected" },
        flexShrink: 0,
        opacity: switching ? 0.6 : 1,
      }}
    >
      <Typography
        variant="chipLabel"
        sx={{
          color: "text.secondary",
          display: "flex",
          alignItems: "center",
          gap: 0.25,
        }}
      >
        {ccyKey.toUpperCase()}
        {switching ? (
          <CircularProgress size={10} thickness={6} sx={{ ml: 0.25 }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
        )}
      </Typography>
    </ButtonBase>
  );

  const heroBlock = isSmall ? (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ minWidth: 0, flex: 1 }}
    >
      <ButtonBase
        onClick={openMenu}
        sx={{
          minWidth: 0,
          alignItems: "center",
          px: 0.75,
          py: 0.5,
          borderRadius: 1.5,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        {amountText}
      </ButtonBase>
      {currencyChip}
    </Stack>
  ) : (
    <Stack direction="column" spacing={0} sx={{ minWidth: 0 }}>
      {heroLabel}
      <Stack direction="row" spacing={0.75} alignItems="baseline">
        {amountText}
        {currencyChip}
      </Stack>
    </Stack>
  );

  const pill = (
    asset: string,
    summary: BalanceResponseDto | undefined,
    fiatValue: number
  ) => (
    <Stack
      key={asset}
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        pl: 0.5,
        pr: 1.5,
        py: 0.5,
        borderRadius: 999,
        bgcolor: "action.hover",
        whiteSpace: "nowrap",
      }}
    >
      <AssetGlyph asset={asset} size={18} />
      <Typography variant="tickerSm" sx={{ color: "text.primary" }}>
        {asset.toUpperCase()}
      </Typography>
      <Typography variant="pageMeta" sx={{ ...tabular, color: "text.primary" }}>
        {cryptoFmt(summary?.available)}
      </Typography>
      <Typography
        variant="cellSecondary"
        sx={{ ...tabular, color: "text.secondary" }}
      >
        · {fiatFmt(fiatValue, ccyKey)}
      </Typography>
    </Stack>
  );

  return (
    <Stack
      direction="row"
      spacing={isSmall ? 1.25 : 2.5}
      alignItems="center"
      sx={{ minWidth: 0, flex: 1 }}
    >
      {heroBlock}

      {!isSmall && (
        <>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ my: 1.25, borderColor: "divider", opacity: 0.6 }}
          />
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ minWidth: 0, overflow: "hidden" }}
          >
            {topHoldings.length === 0 ? (
              <Typography variant="pageMeta" sx={{ color: "text.secondary" }}>
                {t("balance.no_holdings")}
              </Typography>
            ) : (
              topHoldings.map((a) => pill(a.asset, a.summary, a.fiatValue))
            )}
          </Stack>
        </>
      )}

      {!isSmall && (
        <Tooltip title={t("balance.all_assets")}>
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={openMenu}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{
              ...theme.typography.actionLink,
              textTransform: "none",
              borderRadius: 1,
              px: 1.25,
              py: 0.5,
              minWidth: 0,
              whiteSpace: "nowrap",
              flexShrink: 0,
              color: "text.primary",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            {t("balance.all_assets")}
          </Button>
        </Tooltip>
      )}

      {/* currency popover */}
      <Menu
        anchorEl={ccyAnchor}
        open={ccyOpen}
        onClose={closeCcy}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { mt: 1, minWidth: 160, p: 0.5 },
          },
        }}
      >
        <Typography
          variant="sectionLabel"
          sx={{ px: 1.25, py: 0.5, color: "text.secondary" }}
        >
          {t("balance.change_currency")}
        </Typography>
        <Stack spacing={0}>
          {availableCurrencies.map((c) => {
            const isActive = c.toLowerCase() === ccyKey;
            return (
              <ButtonBase
                key={c}
                onClick={() => handleSwitchCurrency(c)}
                sx={{
                  ...theme.typography.cellPrimary,
                  display: "flex",
                  justifyContent: "space-between",
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1,
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? "primary.main" : "text.primary",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <span>{c.toUpperCase()}</span>
                {isActive && (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                    }}
                  />
                )}
              </ButtonBase>
            );
          })}
        </Stack>
      </Menu>

      {/* all-assets menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: { xs: "calc(100vw - 16px)", sm: 400 },
              maxWidth: { xs: "calc(100vw - 16px)", sm: 400 },
              minWidth: { xs: 0, sm: 340 },
              p: 0,
              borderRadius: 2,
              boxShadow: (theme) => theme.shadows[16],
            },
          },
        }}
      >
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5 }}>
          <Typography
            variant="sectionLabel"
            sx={{ color: "text.secondary" }}
            display="block"
          >
            {t("balance.total_balance")}
          </Typography>
          <Typography variant="heroAmount" sx={{ color: "text.primary" }}>
            {fiatFmt(totalFiatSum, ccyKey)}
          </Typography>
        </Box>
        <Divider />
        <Stack
          direction="row"
          sx={{ px: { xs: 1.5, sm: 2 }, py: 0.75, gap: { xs: 0.5, sm: 1 } }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="sectionLabel" sx={{ color: "text.secondary" }}>
              {t("balance.asset", { defaultValue: "Asset" })}
            </Typography>
          </Box>
          <Box
            sx={{
              width: { xs: 64, sm: 90 },
              textAlign: "right",
              flexShrink: 0,
            }}
          >
            <Typography variant="sectionLabel" sx={{ color: "text.secondary" }}>
              {t("balance.rate")}
            </Typography>
          </Box>
          <Box
            sx={{
              width: { xs: 100, sm: 120 },
              textAlign: "right",
              flexShrink: 0,
            }}
          >
            <Typography variant="sectionLabel" sx={{ color: "text.secondary" }}>
              {t("balance.balance", { defaultValue: "Balance" })}
            </Typography>
          </Box>
        </Stack>
        <Divider sx={{ opacity: 0.6 }} />
        <Stack
          sx={{
            maxHeight: 360,
            overflowY: "auto",
            py: 0.5,
          }}
        >
          {orderedAssets.map(({ asset, summary, fiatValue }) => {
            const hasBalance = fiatValue > 0;
            return (
              <Stack
                key={asset}
                direction="row"
                alignItems="center"
                sx={{
                  px: { xs: 1.5, sm: 2 },
                  py: 1,
                  gap: { xs: 0.5, sm: 1 },
                  "&:hover": { bgcolor: "action.hover" },
                  transition: "background-color 120ms",
                }}
              >
                <Stack
                  direction="row"
                  spacing={{ xs: 0.75, sm: 1.25 }}
                  alignItems="center"
                  sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}
                >
                  <AssetGlyph asset={asset} size={22} />
                  <Typography
                    variant="ticker"
                    noWrap
                    sx={{ color: "text.primary" }}
                  >
                    {asset.toUpperCase()}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    width: { xs: 64, sm: 90 },
                    textAlign: "right",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant="pageMeta"
                    noWrap
                    sx={{ ...tabular, color: "text.secondary" }}
                  >
                    {fiatFmt(getRate(asset), ccyKey)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: { xs: 100, sm: 120 },
                    textAlign: "right",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant="amount"
                    noWrap
                    sx={{
                      color: hasBalance ? "text.primary" : "text.disabled",
                    }}
                    display="block"
                  >
                    {cryptoFmt(summary?.available)}
                  </Typography>
                  <Typography
                    variant="cellSecondary"
                    noWrap
                    sx={{
                      ...tabular,
                      color: "text.secondary",
                      display: "block",
                    }}
                  >
                    {fiatFmt(fiatValue, ccyKey)}
                  </Typography>
                </Box>
              </Stack>
            );
          })}
        </Stack>
      </Menu>

      {!isSmall && <Box sx={{ flex: 1 }} />}
    </Stack>
  );
};

export default PortfolioSummary;
